import React, { useMemo, useState } from 'react';
import Table from 'components/Table';
import MetricLabel from 'components/MetricLabel';
import MetricTooltip from 'components/MetricTooltip';
import SegmentTag from 'components/SegmentTag';
import MetricValue from 'components/MetricValue';

import DimensionValue from 'components/DimensionValue';
import DimensionLabel, { getDimensionLabel } from 'components/DimensionLabel';
import useFormatMessage from 'hooks/useFormatMessage';
import { MatomoGoalNamesProvider } from 'hooks/useMatomoGoalNames';
import { gql, useLazyQuery } from '@apollo/client';
import { LoadingOutlined, MinusCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import CenterContent from 'components/CenterContent';
import { getDimensionRawValue, getDisplayValue } from 'shared/utils/dimensionValue';
import { findTotals, getSortOrder, getTableRows, getTotals } from './utils';
import {
  buildNestedQueryVariables,
  getNextLevelFetchSpec,
  isPathSegmentDimension,
  rowCanExpand,
} from './nestedTreePlan';
import messages from './messages';

/**
 * Nested dimension label rows (`nestedTableLevelHeader`) must stay before sibling data rows when sorting.
 * Ant Design negates compare results for `descend`, so branch explicitly for that order.
 */
function compareNestedRowsWithLevelHeaderFirst(a, b, sortOrder, compareRest) {
  if (a.nestedTableLevelHeader !== b.nestedTableLevelHeader) {
    if (sortOrder === 'descend') {
      return a.nestedTableLevelHeader ? 1 : -1;
    }
    return a.nestedTableLevelHeader ? -1 : 1;
  }
  if (a.nestedTableLevelHeader && b.nestedTableLevelHeader) {
    return 0;
  }
  return compareRest(a, b);
}

function sliceForSegment(row, segment, mainSegment) {
  if (!segment) return null;
  const map = row.segmentMetricRows;
  if (map) {
    const found = map[String(segment.id)];
    if (found != null) return found;
  }
  return segment.id === mainSegment?.id ? row : null;
}

/** One column per metric; segment values stacked in rows to align with the Segment column. */
function getMetricColumnNested({
  metric,
  dimensions,
  segments,
  totals,
  mainSegment,
  showChangeOverTime,
  shareOfTotal,
  onBodyCell,
}) {
  const segmentList = segments?.length ? segments : [mainSegment];
  const sortSegment = mainSegment ?? segmentList[0];

  const renderMetricStack = (_, row) => {
    if (row.nestedTableLevelHeader) {
      return <div className="nested-table-level-header-cell-placeholder" aria-hidden />;
    }
    return (
      <div className="nested-table-stacked-cell nested-table-metric-stack">
        {segmentList.map((segment) => {
          const slice = sliceForSegment(row, segment, mainSegment);
          if (!slice) {
            return <div key={segment.id} className="nested-table-stacked-cell__row" />;
          }
          return (
            <div key={segment.id} className="nested-table-stacked-cell__row">
              <MetricTooltip
                metric={metric}
                dimensions={dimensions}
                row={slice}
              >
                <span>
                  <MetricValue
                    metric={metric}
                    row={slice}
                    showChange={showChangeOverTime}
                    showShareOfTotal={shareOfTotal}
                    total={findTotals({ totals, segmentId: segment.id })?.[metric.name]}
                  />
                </span>
              </MetricTooltip>
            </div>
          );
        })}
      </div>
    );
  };

  const type = metric.type || 'number';

  const sorter = segmentList.length
    ? (a, b, sortOrder) => compareNestedRowsWithLevelHeaderFirst(a, b, sortOrder, (ra, rb) => {
      const sa = sliceForSegment(ra, sortSegment, mainSegment);
      const sb = sliceForSegment(rb, sortSegment, mainSegment);
      const va = sa?.[metric.name];
      const vb = sb?.[metric.name];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      const na = Number(va);
      const nb = Number(vb);
      if (Number.isFinite(na) && Number.isFinite(nb)) {
        return na - nb || String(va).localeCompare(String(vb));
      }
      return String(va).localeCompare(String(vb), 'sv', { numeric: true });
    })
    : undefined;

  const defaultSortOrder = metric.sortPriority === 1 ? getSortOrder(metric.sortOrder) : null;

  const totalsTitleRow = totals ? (
    <div className="nested-table-stacked-cell nested-table-metric-stack nested-table-metric-header-totals">
      {segmentList.map((segment) => {
        const result = findTotals({ totals, segmentId: segment.id });
        return (
          <div key={segment.id} className="nested-table-stacked-cell__row">
            {result?.[metric.name] != null ? (
              <MetricTooltip
                metric={metric}
                dimensions={dimensions}
                row={totals}
              >
                <span>
                  <MetricValue
                    metric={metric}
                    row={result}
                    showChange={showChangeOverTime}
                    showShareOfTotal={shareOfTotal}
                    total={totals?.[metric.name]}
                  />
                </span>
              </MetricTooltip>
            ) : null}
          </div>
        );
      })}
    </div>
  ) : null;

  const leafColumn = {
    title: totals ? totalsTitleRow : <MetricLabel {...metric} showTooltip />,
    dataIndex: metric.name,
    key: metric.name,
    type,
    align: 'right',
    className: 'nested-table-metric-col',
    ...(!totals ? { defaultSortOrder, sorter } : {}),
    render: renderMetricStack,
    ...(onBodyCell ? { onCell: onBodyCell } : {}),
  };

  // TableResult-style: metric label on parent row; child row = stacked totals per segment (aligned with segment column stacks).
  if (totals) {
    return {
      title: (
        <div className="nested-table-metric-header-label">
          <MetricLabel {...metric} showTooltip />
        </div>
      ),
      align: 'center',
      className: 'nested-table-metric-col-group',
      key: `${metric.name}-group`,
      type,
      defaultSortOrder,
      sorter,
      children: [leafColumn],
    };
  }

  return leafColumn;
}

const TREE_COLUMN_KEY = 'nestedTreeDimension';

/** Keeps `td` as a normal table cell; flex layout lives inside so row height matches other columns. */
const NestedTableBodyCell = React.forwardRef((props, ref) => {
  const { className, children, ...rest } = props;
  if (className != null && String(className).includes('nested-table-tree-col')) {
    return (
      <td {...rest} ref={ref} className={className}>
        <div className="nested-table-tree-col-flex">
          {children}
        </div>
      </td>
    );
  }
  return <td {...props} ref={ref} />;
});

function getNestedTreeColumnOnCell(record, totalColumnCount) {
  if (record.nestedTableEmptyPlaceholder) {
    return { colSpan: totalColumnCount };
  }
  return {};
}

function createNestedLevelHeaderRow({
  activeDim,
  ancestorTrail,
  mainSegment,
  level,
  parentRowId,
}) {
  const base = parentRowId != null ? String(parentRowId) : 'root';
  return {
    id: `${base}-lvlhdr-${activeDim.name}-${level}`,
    nestedTableLevelHeader: true,
    levelDimensionName: activeDim.name,
    nestedTrail: ancestorTrail,
    [TREE_COLUMN_KEY]: '',
    canExpand: false,
    segmentId: mainSegment.id,
    segment: mainSegment,
    level,
  };
}

/** Merge getRow comparison children into one row with segmentMetricRows (avoids rowspan + expandable conflicts). */
function mergeComparisonChildrenIntoParentRow(row, mainSegment) {
  const { children, ...parentWithoutChildren } = row;
  // primaryData on each child points at the real table row object. Callers may pass a shallow copy of `row`
  // (e.g. { ...row, children }) for merge — that breaks `child.primaryData === row`, so compare via the child ref.
  const parentIdentity = children?.[0]?.primaryData ?? row;
  const segmentMetricRows = {
    [String(mainSegment.id)]: {
      ...parentWithoutChildren,
      segmentId: mainSegment.id,
      segment: mainSegment,
    },
  };
  for (const child of children) {
    if (child.primaryData === parentIdentity && child.segmentId != null) {
      segmentMetricRows[String(child.segmentId)] = {
        ...child,
        segmentId: child.segmentId,
        segment: child.segment,
      };
    }
  }
  return {
    ...parentWithoutChildren,
    segmentMetricRows,
    children: undefined,
  };
}

function flattenSegmentComparisonRows(rows, mainSegment) {
  if (!rows?.length) {
    return rows;
  }
  return rows.map((row) => {
    if (row.nestedTableLevelHeader) {
      return row;
    }
    if (row.children?.length && row.children.every(c => c.primaryData === row)) {
      return mergeComparisonChildrenIntoParentRow(row, mainSegment);
    }
    if (row.children?.length) {
      return {
        ...row,
        children: flattenSegmentComparisonRows(row.children, mainSegment),
      };
    }
    return row;
  });
}

const renderSegment = (segment) => <SegmentTag segment={segment} />;

/** Segment tags stacked (same row structure as metric stacks for alignment). */
function renderSegmentCellStack(segments) {
  const list = segments?.length ? segments : [];
  return (
    <div className="nested-table-stacked-cell nested-table-segment-stack">
      {list.map(s => (
        <div key={s.id} className="nested-table-stacked-cell__row">
          {renderSegment(s)}
        </div>
      ))}
    </div>
  );
}

const nestedTreePopoverProps = {
  truncate: true,
  ellipsisContainerBound: true,
  wrapperClassName: 'nested-table-tree-line__ellipsis-wrap',
  popoverClassNames: { root: 'nested-table-tree-popover' },
  popoverStyles: { root: { maxWidth: 'min(480px, 90vw)' } },
  popoverContentClassName: 'nested-table-tree-popover__body',
};

function renderTreeDimensionColumn(dimensions, $t) {
  return (_, row) => {
    if (row.nestedTableEmptyPlaceholder) {
      return <span className="nested-table-empty-nested-msg">{$t(messages.nestedTableEmptyNested)}</span>;
    }
    if (row.nestedTableLevelHeader) {
      return (
        <div className="nested-table-tree-line nested-table-level-header-row">
          <DimensionLabel
            dimension={row.levelDimensionName}
            name={row.levelDimensionName}
            showTooltip
          />
        </div>
      );
    }
    const trail = row.nestedTrail || [];
    const entry = trail[trail.length - 1];
    if (!entry) {
      return null;
    }
    const dim = dimensions.find(d => d.name === entry.name);

    if (!dim) {
      const raw = String(entry.value ?? '');
      return (
        <div className="nested-table-tree-line">
          <DimensionValue
            dimension={{ name: entry.name, field: entry.name }}
            value={raw}
            row={row}
            {...nestedTreePopoverProps}
          />
        </div>
      );
    }

    const rawValue = entry.value;
    let out = String(rawValue ?? '');
    if (isPathSegmentDimension(dim)) {
      const prefix = row.parentValue != null ? row.parentValue : '/';
      if (prefix && out.startsWith(prefix)) {
        out = out.slice(prefix.length);
      }
      out = out.replace(/\?$/, '');
    }

    return (
      <div className="nested-table-tree-line">
        <DimensionValue
          dimension={dim}
          value={out}
          row={row}
          popoverValue={rawValue}
          {...nestedTreePopoverProps}
        />
      </div>
    );
  };
}

function getNestedTreeColumnWidth(dimensions) {
  const maxW = dimensions.reduce((m, d) => Math.max(m, d.width || 0), 0);
  return maxW > 0 ? maxW : undefined;
}

function countLeafColumns(columns) {
  return columns.reduce((n, col) => n + (col.children?.length || 1), 0) || 1;
}

const getColumns = ({
  comparisonSegments,
  dimensionTreeTitle,
  dimensions,
  metrics,
  totals,
  segments,
  mainSegment,
  showChangeOverTime,
  shareOfTotal,
  $t,
}) => {
  const columns = [];

  const useGroupedTotalsHeader = Boolean(totals && metrics.length > 0);

  if (dimensions.length > 0) {
    const sortDim = dimensions.find(d => d.sortPriority === 1);
    const treeWidth = getNestedTreeColumnWidth(dimensions);
    const treeLeaf = {
      title: useGroupedTotalsHeader ? $t(messages.totals) : dimensionTreeTitle,
      dataIndex: TREE_COLUMN_KEY,
      key: TREE_COLUMN_KEY,
      className: `nested-table-tree-col${useGroupedTotalsHeader ? ' nested-table-header-sub-cell' : ''}`,
      ellipsis: true,
      ...(treeWidth != null
        ? { width: treeWidth, minWidth: Math.min(treeWidth, 160), maxWidth: treeWidth }
        : { width: '32%', minWidth: 200, maxWidth: 560 }),
      defaultSortOrder: sortDim ? getSortOrder(sortDim.sortOrder) : null,
      sorter: (a, b, sortOrder) => compareNestedRowsWithLevelHeaderFirst(a, b, sortOrder, (ra, rb) => (
        String(ra[TREE_COLUMN_KEY] ?? '').localeCompare(String(rb[TREE_COLUMN_KEY] ?? ''))
      )),
      render: renderTreeDimensionColumn(dimensions, $t),
    };
    if (useGroupedTotalsHeader) {
      columns.push({
        title: dimensionTreeTitle,
        align: 'left',
        className: 'nested-table-tree-col-group',
        key: `${TREE_COLUMN_KEY}-group`,
        children: [treeLeaf],
      });
    } else {
      columns.push(treeLeaf);
    }
  }

  if (comparisonSegments?.length > 0) {
    const segmentList = segments?.length ? segments : [mainSegment];
    const segmentTitle = (
      <div className="nested-table-segment-header-title">
        <div className="nested-table-segment-header-label">
          {$t(messages.segment)}
        </div>
      </div>
    );
    const segmentLeaf = {
      title: useGroupedTotalsHeader ? renderSegmentCellStack(segmentList) : segmentTitle,
      key: 'segment-stack',
      dataIndex: 'segment',
      className: `nested-table-segment-col${useGroupedTotalsHeader ? ' nested-table-header-sub-cell' : ''}`,
      sorter: false,
      render: (_, record) => (record.nestedTableLevelHeader
        ? <div className="nested-table-level-header-cell-placeholder" aria-hidden />
        : renderSegmentCellStack(segmentList)),
      onCell: (record) => (record.nestedTableEmptyPlaceholder ? { colSpan: 0 } : {}),
    };
    if (useGroupedTotalsHeader) {
      columns.push({
        title: segmentTitle,
        align: 'center',
        className: 'nested-table-segment-col-group',
        key: 'segment-stack-group',
        sorter: false,
        children: [segmentLeaf],
      });
    } else {
      columns.push(segmentLeaf);
    }
  }

  if (metrics.length > 0) {
    const emptyNestedMetricCell = (record) => (record.nestedTableEmptyPlaceholder ? { colSpan: 0 } : {});
    columns.push(...metrics.map(metric => getMetricColumnNested({
      metric,
      dimensions,
      segments,
      totals,
      mainSegment,
      showChangeOverTime,
      shareOfTotal,
      onBodyCell: emptyNestedMetricCell,
    })));
  }

  if (dimensions.length > 0) {
    const leafCount = countLeafColumns(columns);
    const treeGroup = columns[0];
    const treeLeaf = treeGroup.children ? treeGroup.children[0] : treeGroup;
    treeLeaf.onCell = (record) => getNestedTreeColumnOnCell(record, leafCount);
  }

  return columns;
};

const RESULT_QUERY = gql`
  query CustomResult($input: ResultsInput!) {
    results(input: $input) {
      segmentId
      data
      totals
    }
  }
`;

function createNestedEmptyPlaceholderRow({ parentRowId, mainSegment, level, ancestorTrail }) {
  return {
    id: `${parentRowId}-nested-empty`,
    nestedTableEmptyPlaceholder: true,
    nestedTrail: ancestorTrail,
    [TREE_COLUMN_KEY]: '',
    canExpand: false,
    segmentId: mainSegment.id,
    segment: mainSegment,
    level,
    rowId: 'nested-empty',
  };
}

function buildNestedRows({
  fullDimensions,
  mainSegment,
  comparisonSegments,
  results,
  expandedRowsData,
  ancestorTrail,
  level,
  parentRowId,
}) {
  const spec = getNextLevelFetchSpec(fullDimensions, ancestorTrail);
  if (!spec) {
    return [];
  }

  const { dimensionsInput, activeDim, activeKind } = spec;

  const rowsFromApi = getTableRows({
    dimensions: dimensionsInput,
    mainSegment,
    comparisonSegments,
    results,
    level,
  });

  if (!rowsFromApi?.length) {
    if (ancestorTrail.length > 0 && parentRowId) {
      return [createNestedEmptyPlaceholderRow({ parentRowId, mainSegment, level, ancestorTrail })];
    }
    return [];
  }

  const headerRows = (ancestorTrail.length > 0 && !isPathSegmentDimension(activeDim))
    ? [createNestedLevelHeaderRow({ activeDim, ancestorTrail, mainSegment, level, parentRowId })]
    : [];

  const kind = activeKind === 'pathSegment' ? 'pathSegment' : 'scalar';
  const prevSamePath = [...ancestorTrail].reverse().find(t => t.name === activeDim.name && t.kind === 'pathSegment');
  const parentValue = prevSamePath?.value ?? '/';

  const dataRows = rowsFromApi.map((row) => {
    const value = getDisplayValue(row[activeDim.name]);
    const trailEntry = { name: activeDim.name, value, rawValue: getDimensionRawValue(row[activeDim.name]), kind };
    if (kind === 'pathSegment' && spec.pathIndexByName[activeDim.name] != null) {
      trailEntry.pathSqlIndex = spec.pathIndexByName[activeDim.name];
    }
    const nestedTrail = [...ancestorTrail, trailEntry];
    const canExpand = rowCanExpand(fullDimensions, nestedTrail);

    let { children } = row;
    let segmentMetricRows;

    if (expandedRowsData[row.id]) {
      const comparisonChildren = row.children;
      const nestedDimensionRows = buildNestedRows({
        fullDimensions,
        mainSegment,
        comparisonSegments,
        results: expandedRowsData[row.id],
        expandedRowsData,
        ancestorTrail: nestedTrail,
        level: level + 1,
        parentRowId: row.id,
      });
      const useNested = nestedDimensionRows?.length > 0;
      if (useNested) {
        const slicesForMerge = row.segmentComparisonResults?.length
          ? row.segmentComparisonResults
          : comparisonChildren;
        if (slicesForMerge?.length) {
          const merged = mergeComparisonChildrenIntoParentRow(
            { ...row, children: slicesForMerge },
            mainSegment,
          );
          segmentMetricRows = merged.segmentMetricRows;
        }
      }
      children = useNested ? nestedDimensionRows : comparisonChildren;
    } else if (children?.length && children.every(c => c.primaryData === row)) {
      // Merge comparison children now — the immutable row spread below breaks
      // the reference equality that flattenSegmentComparisonRows relies on.
      const merged = mergeComparisonChildrenIntoParentRow(row, mainSegment);
      segmentMetricRows = merged.segmentMetricRows;
      children = undefined;
    }

    return {
      ...row,
      parentValue,
      nestedTrail,
      [TREE_COLUMN_KEY]: value,
      canExpand,
      children,
      ...(segmentMetricRows != null ? { segmentMetricRows } : {}),
    };
  });

  return [...headerRows, ...dataRows];
}

const NestedTableResult = ({ results, config, segments, mainSegment, comparisonSegments, variables }) => {
  const $t = useFormatMessage();
  const { dimensions = [], metrics = [], settings } = config || {};
  const { showChangeOverTime, shareOfTotal, pageLimit } = settings || {};
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [expandedRowsData, setExpandedRowsData] = useState({});
  const [getNestedResult] = useLazyQuery(RESULT_QUERY);

  const rows = useMemo(() => {
    const built = buildNestedRows({
      fullDimensions: dimensions,
      mainSegment,
      comparisonSegments,
      results,
      expandedRowsData,
      ancestorTrail: [],
      level: 1,
      parentRowId: undefined,
    });
    return mainSegment ? flattenSegmentComparisonRows(built, mainSegment) : built;
  }, [results, dimensions, mainSegment, comparisonSegments, expandedRowsData]);

  const totals = useMemo(() => (rows.length > 1 ? getTotals({ mainSegment, comparisonSegments, results }) : null), [mainSegment, comparisonSegments, results, rows.length]);

  const allResults = useMemo(() => {
    const expanded = Object.values(expandedRowsData).flat();
    return [...(results || []), ...expanded];
  }, [results, expandedRowsData]);

  const dimensionTreeTitle = useMemo(
    () => (dimensions[0]
      ? getDimensionLabel({ $t, dimension: dimensions[0].name, name: dimensions[0].name })
      : ''),
    [dimensions, $t],
  );

  const columns = useMemo(() => getColumns({
    dimensionTreeTitle,
    dimensions,
    metrics,
    mainSegment,
    comparisonSegments,
    totals,
    segments,
    showChangeOverTime: showChangeOverTime && mainSegment?.dateRangeSegments?.length > 0,
    shareOfTotal,
    $t,
  }), [dimensionTreeTitle, dimensions, metrics, mainSegment, comparisonSegments, totals, segments, showChangeOverTime, shareOfTotal, $t]);

  const onRowExpand = async (expanded, row) => {
    if (!row.canExpand) {
      return;
    }
    setExpandedRowKeys(keys => (expanded ? [...keys, row.id] : keys.filter(id => id !== row.id)));
    if (expanded && !expandedRowsData[row.id]) {
      const nextVars = buildNestedQueryVariables(variables.input, dimensions, row.nestedTrail);
      if (!nextVars) {
        setExpandedRowKeys(keys => keys.filter(id => id !== row.id));
        return;
      }
      try {
        const nestedResult = await getNestedResult({ variables: nextVars });
        setExpandedRowsData(prev => ({ ...prev, [row.id]: nestedResult.data?.results }));
      } catch {
        setExpandedRowKeys(keys => keys.filter(id => id !== row.id));
      }
    }
  };

  return (
    <MatomoGoalNamesProvider results={allResults} dimensions={dimensions}>
      <CenterContent horizontal style={{ padding: '8px 0' }}>
        {segments.map(s => <SegmentTag key={s.id} segment={s} showDateRange />)}
      </CenterContent>
      <Table
        className="table-result nested-table-result"
        interactive={false}
        columns={columns}
        dataSource={rows}
        rowClassName={(record) => (record.nestedTableLevelHeader
          ? 'editable-row nested-table-dimension-label-row'
          : 'editable-row')}
        components={{ body: { cell: NestedTableBodyCell } }}
        scroll={{
          // max-content prevents column max-width from binding — cells grow with text and ellipsis never shows
          x: true,
        }}
        expandable={{
          expandedRowKeys,
          onExpand: onRowExpand,
          indentSize: 16,
          expandIcon: ({ expanded, record }) => {
            if (!record.canExpand) {
              return <span className="nested-table-expand-icon-wrap"><span className="nested-table-expand-spacer" /></span>;
            }
            if (expanded && !expandedRowsData[record.id]) {
              return <span className="nested-table-expand-icon-wrap"><LoadingOutlined /></span>;
            }
            return (
              <span className="nested-table-expand-icon-wrap">
                {expanded ? <MinusCircleOutlined /> : <PlusCircleOutlined />}
              </span>
            );
          },
          expandRowByClick: true,
        }}
        showSorterTooltip={false}
        pagination={pageLimit ? {
          hideOnSinglePage: true,
          defaultPageSize: pageLimit,
          pageSizeOptions: [5, 10, 50, 100],
        } : false}
      />
    </MatomoGoalNamesProvider>
  );
};

export default NestedTableResult;
